import React from 'react';
import { observer } from 'mobx-react';
import { useFormikContext } from 'formik';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { policyType } from '@ourbranch/lookups';
import { useStore } from 'core/store';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { numberFormatter } from 'core/helpers/formatters';
import { getPolicyLines } from './policy-cards/policy-lines';
import { PolicyCards } from './policy-cards';
import { getFarmPolicyMask } from 'common/helpers/farm-helpers';
import useStyles from './price-summary.styles';

const PriceSummary = observer(() => {
  const classes = useStyles();
  const { offer: store } = useStore();
  const { values, dirty } = useFormikContext();
  const selectedOption = values.selectedOption;
  const lines = getPolicyLines({
    selectedOption,
    options: store.offer.options,
    global: store.offer.quote.global,
    state: store.state
  });
  const option = store.offer.options?.find((item) => item.type === selectedOption);
  if (isFarmBasedPolicyType(selectedOption) || selectedOption === policyType.FABundle) {
    lines.unshift({
      key: 'farm',
      title: getFarmPolicyMask(selectedOption, store.state)?.tabLabel || 'Farm',
      total: option?.farmTotal,
      termMonths: 12
    });
  }
  if (selectedOption?.includes(policyType.Auto) && !lines.some((line) => line.key === 'auto')) {
    lines.push({ key: 'auto', title: 'Auto', total: null, termMonths: 6 });
  }
  const priced = lines.length > 0 && lines.every((line) => Number.isFinite(line.total));
  const current = priced && store.isStale === false && !dirty && !store.stalePaymentScheduleDate;
  const bundle = [policyType.HABundle, policyType.CABundle, policyType.ARBundle, policyType.FABundle].includes(
    selectedOption
  );
  let status = current ? 'Prices are current.' : 'Update price before purchase.';
  if (store.recalculatingPrice) {
    status = 'Updating price…';
  }
  return (
    <>
      <Card className={classes.card}>
        <div className={classes.header}>
          <Label type="body1" component="h2" className={classes.heading}>
            Price
          </Label>
          <span className={classes.id}>{store.offer.webUserId || store.offer.code}</span>
        </div>
        {lines.map((line) => (
          <div className={classes.row} key={line.key}>
            <Label type="body2">{line.title}</Label>
            <div className={classes.amount}>
              <strong>{Number.isFinite(line.total) ? `$${numberFormatter(line.total, 2)}` : 'Not available'}</strong>
              <span>per {line.termMonths} months</span>
            </div>
          </div>
        ))}
        {!lines.length && <Label type="body2">Price unavailable</Label>}
        {bundle && priced && <Label type="body2">Bundle price shown.</Label>}
        <Label type="body2" className={classes.status}>
          {status}
        </Label>
      </Card>
      {lines.length > 0 && (
        <details className={classes.details}>
          <summary>Payment details & schedules</summary>
          <PolicyCards />
        </details>
      )}
    </>
  );
});
export default PriceSummary;
